import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import type { UserRole } from "@/hooks/useAuth";

type Role = NonNullable<UserRole>;

const roles: { key: Role; label: string; desc: string; icon: string; color: string }[] = [
  { key: "admin", label: "系统管理员", desc: "总系统管理，多租户配置", icon: "ri-shield-star-line", color: "text-[#E8531A]" },
  { key: "operator", label: "运营人员", desc: "日常业务操作与管理", icon: "ri-user-settings-line", color: "text-amber-600" },
  { key: "viewer", label: "数据查看员", desc: "只读查看报表与数据", icon: "ri-eye-line", color: "text-green-600" },
];

const demoAccounts = [
  { role: "admin" as Role, username: "admin", password: "admin123", tenant: "总管理平台", displayName: "超级管理员" },
  { role: "operator" as Role, username: "operator", password: "op123456", tenant: "朝阳燃气公司", displayName: "运营专员" },
  { role: "viewer" as Role, username: "viewer", password: "view123", tenant: "朝阳燃气公司", displayName: "数据查看员" },
];

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  // Get the page user was trying to access before being redirected to login
  const from = (location.state as { from?: string })?.from || "/";
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role>("operator");
  const [matchedAccount, setMatchedAccount] = useState<typeof demoAccounts[0] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState<"login" | "role">("login");

  const handleLogin = () => {
    if (!username.trim() || !password.trim()) {
      setError("请输入用户名和密码");
      return;
    }
    setLoading(true);
    setError("");
    setTimeout(() => {
      const account = demoAccounts.find(a => a.username === username && a.password === password);
      if (account) {
        setMatchedAccount(account);
        setSelectedRole(account.role);
        setLoading(false);
        setStep("role");
      } else {
        setError("用户名或密码错误，请重试");
        setLoading(false);
      }
    }, 1200);
  };

  const handleEnter = () => {
    setLoading(true);
    const acc = matchedAccount;
    login({
      username,
      role: selectedRole,
      tenant: acc?.tenant || "朝阳燃气公司",
      displayName: acc?.displayName || username,
    });
    setTimeout(() => {
      // Redirect back to the page user was trying to access, or home
      navigate(from, { replace: true });
    }, 800);
  };

  const fillDemo = (acc: typeof demoAccounts[0]) => {
    setUsername(acc.username);
    setPassword(acc.password);
    setError("");
  };

  return (
    <div className="min-h-screen flex" style={{ background: "linear-gradient(135deg, #0f1729 0%, #1a2540 50%, #0f1729 100%)" }}>
      {/* Left panel */}
      <div className="flex-1 flex flex-col justify-center px-16 py-12 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full opacity-5" style={{ background: "radial-gradient(circle, #E8531A, transparent)" }}></div>
          <div className="absolute bottom-1/4 right-1/4 w-64 h-64 rounded-full opacity-5" style={{ background: "radial-gradient(circle, #E8531A, transparent)" }}></div>
          {/* Grid lines */}
          <svg className="absolute inset-0 w-full h-full opacity-5" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="0.5"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        <div className="relative z-10 max-w-lg">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-12">
            <div className="w-12 h-12 rounded-2xl bg-[#E8531A] flex items-center justify-center">
              <i className="ri-fire-line text-white text-2xl"></i>
            </div>
            <div>
              <div className="text-white font-bold text-xl tracking-wide">AI智慧燃气</div>
              <div className="text-white/40 text-sm">SaaS营收管理平台 v3.0</div>
            </div>
          </div>

          <h1 className="text-4xl font-bold text-white mb-4 leading-tight">
            智能燃气管理<br />
            <span className="text-[#E8531A]">AI驱动</span> 全面升级
          </h1>
          <p className="text-white/50 text-base mb-10 leading-relaxed">
            集成AI分析、多租户管理、实时监控于一体的<br />新一代智慧燃气SaaS平台
          </p>

          {/* Feature highlights */}
          <div className="grid grid-cols-2 gap-4">
            {[
              { icon: "ri-robot-2-line", label: "AI智能分析", desc: "深度学习驱动的数据洞察" },
              { icon: "ri-building-2-line", label: "多租户管理", desc: "灵活的企业级SaaS架构" },
              { icon: "ri-map-2-line", label: "GIS实时监控", desc: "管网全景可视化监控" },
              { icon: "ri-shield-check-line", label: "安全合规", desc: "企业级数据安全保障" },
            ].map((f, i) => (
              <div key={i} className="flex items-start gap-3 p-4 rounded-xl" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
                <div className="w-8 h-8 rounded-lg bg-[#E8531A]/20 flex items-center justify-center flex-shrink-0">
                  <i className={`${f.icon} text-[#E8531A] text-base`}></i>
                </div>
                <div>
                  <div className="text-white text-sm font-medium">{f.label}</div>
                  <div className="text-white/40 text-xs mt-0.5">{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right panel - Login form */}
      <div className="w-[440px] flex items-center justify-center px-10 py-12" style={{ background: "rgba(255,255,255,0.03)", borderLeft: "1px solid rgba(255,255,255,0.08)" }}>
        <div className="w-full">
          {step === "login" ? (
            <>
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-white mb-1">欢迎登录</h2>
                <p className="text-white/40 text-sm">请输入您的账号信息</p>
              </div>

              {/* Demo accounts */}
              <div className="mb-6">
                <div className="text-white/30 text-xs mb-2">快速体验（点击填入）</div>
                <div className="flex gap-2">
                  {demoAccounts.map((acc, i) => (
                    <button
                      key={i}
                      onClick={() => fillDemo(acc)}
                      className="flex-1 py-2 px-2 rounded-lg text-[10px] text-center cursor-pointer transition-all whitespace-nowrap"
                      style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.6)" }}
                    >
                      {acc.role === "admin" ? "管理员" : acc.role === "operator" ? "运营员" : "查看员"}
                    </button>
                  ))}
                </div>
              </div>

              {/* Form */}
              <div className="space-y-4">
                <div>
                  <label className="text-white/50 text-xs mb-1.5 block">用户名</label>
                  <div className="relative">
                    <i className="ri-user-3-line absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30 text-sm"></i>
                    <input
                      type="text"
                      value={username}
                      onChange={e => { setUsername(e.target.value); setError(""); }}
                      onKeyDown={e => e.key === "Enter" && handleLogin()}
                      placeholder="请输入用户名"
                      className="w-full pl-10 pr-4 py-3 rounded-xl text-sm text-white placeholder-white/20 focus:outline-none transition-all"
                      style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.12)" }}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-white/50 text-xs mb-1.5 block">密码</label>
                  <div className="relative">
                    <i className="ri-lock-line absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30 text-sm"></i>
                    <input
                      type={showPwd ? "text" : "password"}
                      value={password}
                      onChange={e => { setPassword(e.target.value); setError(""); }}
                      onKeyDown={e => e.key === "Enter" && handleLogin()}
                      placeholder="请输入密码"
                      className="w-full pl-10 pr-10 py-3 rounded-xl text-sm text-white placeholder-white/20 focus:outline-none transition-all"
                      style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.12)" }}
                    />
                    <button
                      onClick={() => setShowPwd(!showPwd)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 cursor-pointer"
                    >
                      <i className={`${showPwd ? "ri-eye-off-line" : "ri-eye-line"} text-white/30 text-sm`}></i>
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="flex items-center gap-2 text-red-400 text-xs bg-red-500/10 rounded-lg px-3 py-2">
                    <i className="ri-error-warning-line"></i>
                    {error}
                  </div>
                )}

                <div className="flex items-center justify-between text-xs">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" className="rounded" />
                    <span className="text-white/40">记住登录状态</span>
                  </label>
                  <button className="text-[#E8531A] cursor-pointer whitespace-nowrap">忘记密码？</button>
                </div>

                <button
                  onClick={handleLogin}
                  disabled={loading}
                  className="w-full py-3 rounded-xl text-sm font-semibold text-white transition-all cursor-pointer whitespace-nowrap"
                  style={{ background: loading ? "rgba(232,83,26,0.5)" : "#E8531A" }}
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      验证中...
                    </span>
                  ) : "登录系统"}
                </button>
              </div>

              <div className="mt-6 text-center text-white/20 text-xs">
                AI智慧燃气SaaS平台 · 企业版 · 数据安全加密传输
              </div>
            </>
          ) : (
            /* Role selection after login */
            <>
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                    <i className="ri-check-line text-white text-xs"></i>
                  </div>
                  <h2 className="text-xl font-bold text-white">身份验证成功</h2>
                </div>
                <p className="text-white/40 text-sm">请选择您的登录角色</p>
              </div>

              <div className="space-y-3 mb-6">
                {roles.map(role => (
                  <button
                    key={role.key}
                    onClick={() => setSelectedRole(role.key)}
                    className={`w-full flex items-center gap-4 p-4 rounded-xl text-left cursor-pointer transition-all ${selectedRole === role.key ? "border-[#E8531A]" : "border-transparent"}`}
                    style={{
                      background: selectedRole === role.key ? "rgba(232,83,26,0.1)" : "rgba(255,255,255,0.04)",
                      border: `1px solid ${selectedRole === role.key ? "#E8531A" : "rgba(255,255,255,0.08)"}`
                    }}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${selectedRole === role.key ? "bg-[#E8531A]" : "bg-white/10"}`}>
                      <i className={`${role.icon} ${selectedRole === role.key ? "text-white" : role.color} text-lg`}></i>
                    </div>
                    <div className="flex-1">
                      <div className="text-white text-sm font-semibold">{role.label}</div>
                      <div className="text-white/40 text-xs mt-0.5">{role.desc}</div>
                    </div>
                    {selectedRole === role.key && (
                      <div className="w-5 h-5 rounded-full bg-[#E8531A] flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line text-white text-xs"></i>
                      </div>
                    )}
                  </button>
                ))}
              </div>

              <button
                onClick={handleEnter}
                disabled={loading}
                className="w-full py-3 rounded-xl text-sm font-semibold text-white transition-all cursor-pointer whitespace-nowrap"
                style={{ background: "#E8531A" }}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    进入系统...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-login-box-line"></i>
                    进入系统
                  </span>
                )}
              </button>

              <button
                onClick={() => setStep("login")}
                className="w-full mt-3 py-2.5 rounded-xl text-sm text-white/40 cursor-pointer hover:text-white/60 transition-colors whitespace-nowrap"
              >
                返回登录
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
